'use client'

import { useState } from 'react'
import { useCachedSWR } from '@/core/lib/swr/use-cached-swr'
import { appSwrKey } from '@/features/veluvit/lib/app-read'
import { useAppReadScope } from '@/features/veluvit/hooks/use-app-read-scope'
import { paymentOrdersReadSchema } from '@/features/veluvit/schemas/payment-orders-read.schema'
import type { FetcherResponse } from '@/core/lib/fetcher/core'

import { Button } from '@/core/components/form/button/button'
import Spinner from '@/core/components/spinner'
import EmptyState from '@/features/veluvit/components/ui/empty-state'
import PaymentOrderPanel, {
  PAYMENT_STATUS_LABELS,
} from '@/features/veluvit/components/wallet/payment-order-panel'
import { openCharge } from '@/features/veluvit/services/wallet.actions'
import { useSessionStore } from '@/features/veluvit/stores/session.store'

import type { PaymentOrder } from '@/features/veluvit/schemas/payment.schema'

export default function PaymentHistoryPanel({
  ownerId,
  epoch,
  initial,
}: {
  ownerId: string
  epoch?: string
  initial?: FetcherResponse<PaymentOrder[]>
}) {
  const [selected, setSelected] = useState<string | null>(null)
  const identity = useSessionStore((state) => state.identity)
  const currentOwner = useSessionStore((state) => state.data?.user?.id)
  const pageScope = useAppReadScope()
  const scope =
    currentOwner === ownerId
      ? (pageScope ?? (epoch ? `${epoch}:${identity}` : null))
      : null
  const key = scope ? appSwrKey(scope, '/payment/order/') : null
  const {
    data,
    error: requestError,
    mutate,
  } = useCachedSWR<PaymentOrder[]>(key, 'REALTIME', {
    responseSchema: paymentOrdersReadSchema,
    ...(scope === epoch && initial
      ? {
          fallbackData: initial,
          ...(initial.ok ? {} : { revalidateOnMount: false }),
        }
      : {}),
  })
  const orders = currentOwner === ownerId && data?.ok ? data.data : null
  const error =
    currentOwner === ownerId
      ? requestError?.message || (data && !data.ok ? data.message : '')
      : ''

  if (selected)
    return (
      <section className="space-y-4">
        <Button variant="ghost" size="sm" onClick={() => setSelected(null)}>
          결제 내역으로
        </Button>
        <PaymentOrderPanel
          key={selected}
          ownerId={ownerId}
          orderId={selected}
        />
      </section>
    )

  return (
    <section aria-label="결제 내역">
      {!orders && !error && (
        <div className="flex justify-center py-8">
          <Spinner />
        </div>
      )}
      {error && (
        <p role="alert" className="mt-4 text-c14 leading-6 text-error">
          {error}
        </p>
      )}
      {orders?.length === 0 && (
        <EmptyState
          title="아직 결제 내역이 없어요"
          description="충전한 상품과 결제 상태를 여기서 확인할 수 있어요."
          action={
            <Button skin="flat" variant="secondary" onClick={openCharge}>
              충전소 둘러보기
            </Button>
          }
        />
      )}
      <ul className="divide-y divide-border">
        {orders?.map((order) => (
          <li key={order.id}>
            <Button
              variant="ghost"
              className="w-full text-left"
              onClick={() => setSelected(order.id)}
            >
              <span className="flex w-full items-start justify-between gap-4 py-3">
                <span className="min-w-0">
                  <span className="block text-c15 font-semibold text-foreground over10:text-c16">
                    {order.product.name}
                  </span>
                  <span className="mt-1.5 block text-c13 text-description over10:text-c14">
                    {PAYMENT_STATUS_LABELS[order.status]}
                  </span>
                </span>
                <span className="shrink-0 text-c15 font-semibold tabular-nums text-foreground">
                  {order.product.amount.toLocaleString('ko-KR')}원
                </span>
              </span>
            </Button>
          </li>
        ))}
      </ul>
      <Button
        className="mt-5"
        variant="ghost"
        size="sm"
        onClick={() => void mutate()}
      >
        내역 새로고침
      </Button>
    </section>
  )
}
